import { useState } from 'react';

import { Column } from 'react-table';
import ProductRegistrationPopup from './ProductRegistrationPopup';
import ProductEditPopup from './ProductEditPopup';
import ProductViewPopup from './ProductViewPopup';
import DataTable from '../../DataTable/DataTable';
import SearchComponent from '../../DataTable/SearchComponent';
import ActivationModal from '../../Modals/ActivationModal';
import { useProduct } from '../../../hooks/useProduct';
import useSystemFeatures from '../../../hooks/system/useSystemFeatures';
import { SYSTEM_FEATURE } from '../../../types/system/features';

const ProductTable = () => {
  const { isEnabled } = useSystemFeatures();
  const showRestaurantCharges = isEnabled(SYSTEM_FEATURE.RESTAURANT);

  const {
    handlerSearchProducts,
    handlerToggleProductState,
    handlerGetProductById,
    products,
    paginator,
    updatePageSize,
    pageSize,

    isCreateProductModalOpen,
    openCreateProductModal,
    closeCreateProductModal,

    isEditProductModalOpen,
    openEditProductModal,
    closeEditProductModal,

    isViewProductModalOpen,
    openViewProductModal,
    closeViewProductModal,
  } = useProduct();

  // Estados para los modales
  const [isActivationModalOpen, setIsActivationModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<{
    id: number;
    status: boolean;
  } | null>(null);

  // 📌 Función para manejar la activación/desactivación de producto
  const handleToggleProductStatus = (product: {
    id: number;
    status: boolean;
  }) => {
    setSelectedProduct(product);
    setIsActivationModalOpen(true);
  };

  // 📌 Confirmar activación/desactivación
  const handleConfirmToggleStatus = () => {
    if (selectedProduct) {
      handlerToggleProductState(selectedProduct.id);
    }
    setIsActivationModalOpen(false);
  };

  // 📌 Cancelar activación/desactivación
  const handleCancelToggle = () => {
    setIsActivationModalOpen(false);
    setSelectedProduct(null);
  };

  const handleViewProduct = (product) => {
    handlerGetProductById(product.id);
    openViewProductModal();
  };

  // 📌 Abrir modal de edición con datos actualizados del producto
  const handleEditProduct = (product) => {
    handlerGetProductById(product.id);
    openEditProductModal();
  };

  // 📌 Botón de agregar producto o servicio
  const addButton = (
    <button
      className="inline-flex items-center rounded-md bg-primary px-4 py-2 text-white hover:bg-opacity-90"
      onClick={() => openCreateProductModal()}
    >
      + Nuevo Ítem
    </button>
  );

  // 📌 Formatear valores numéricos a moneda USD con separadores de miles
  const formatCurrency = (value: number) => {
    if (typeof value !== 'number') return '$0.00'; // Manejo de valores no numéricos
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const actions = [
    { key: 'edit', onClick: handleEditProduct },
    { key: 'toggle', onClick: handleToggleProductStatus },
    { key: 'view', onClick: handleViewProduct },
  ];

  // 📌 Columnas de la tabla con formato de moneda
  const columns: Column<object>[] = [
    { Header: 'Código', accessor: 'code' },
    { Header: 'Nombre', accessor: 'name' },
    {
      Header: 'Precio',
      accessor: 'price',
      Cell: ({ value }) => (
        <span className="text-right">{formatCurrency(value)}</span>
      ),
    },
    {
      Header: 'Costo',
      accessor: 'cost',
      Cell: ({ value }) => (
        <span className="text-right">{formatCurrency(value)}</span>
      ),
    },
    {
      Header: 'Stock',
      accessor: 'stock',
      Cell: ({ value }) => (
        <span className="text-right">{value != null ? value : 0}</span>
      ),
    },
    ...(showRestaurantCharges
      ? [
          {
            Header: 'Cargos adicionales',
            accessor: 'tipEligible',
            Cell: ({ row }: any) => (
              <div className="flex min-w-[170px] flex-wrap gap-1.5">
                {row.original.tipEligible && (
                  <span className="rounded-full bg-primary/10 px-2 py-1 text-xs font-medium text-primary">
                    Propina
                  </span>
                )}
                {row.original.appliesTourismTax && (
                  <span className="rounded-full bg-warning/10 px-2 py-1 text-xs font-medium text-warning">
                    Turismo 5 %
                  </span>
                )}
                {!row.original.tipEligible &&
                  !row.original.appliesTourismTax && (
                    <span className="text-xs text-bodydark2">No aplica</span>
                  )}
              </div>
            ),
          } as Column<object>,
        ]
      : []),
    {
      Header: 'Estado',
      accessor: 'status',
      Cell: ({ value }) => (
        <span
          className={`inline-flex rounded-full px-3 py-1 text-sm font-medium ${
            value ? 'bg-green-500 text-white' : 'bg-[#DC3545] text-white'
          }`}
        >
          {value ? 'Activo' : 'Inactivo'}
        </span>
      ),
    },
  ];

  return (
    <>
      <DataTable
        columns={columns}
        data={products}
        actions={actions}
        pageSizeOptions={[5, 10, 20, 50]}
        headerButton={addButton}
        url="/catalog/items"
        paginator={paginator}
        searchComponent={
          <SearchComponent
            onSearch={handlerSearchProducts}
            pageSize={pageSize}
          />
        }
        onPageSizeChange={updatePageSize}
      />

      {/* Modal para activar/desactivar producto */}
      <ActivationModal
        isOpen={isActivationModalOpen}
        title={
          selectedProduct?.status
            ? '¿Desactivar Producto?'
            : '¿Activar Producto?'
        }
        message={
          selectedProduct?.status
            ? '¿Estás seguro de que deseas desactivar este producto?'
            : '¿Estás seguro de que deseas activar este producto?'
        }
        confirmText={selectedProduct?.status ? 'Desactivar' : 'Activar'}
        cancelText="Cancelar"
        onConfirm={handleConfirmToggleStatus}
        onCancel={handleCancelToggle}
      />

      {/* Modal para registrar producto */}
      <ProductRegistrationPopup
        popupOpen={isCreateProductModalOpen}
        onClose={closeCreateProductModal}
      />

      {/* Modal para editar producto */}
      <ProductEditPopup
        popupOpen={isEditProductModalOpen}
        onClose={closeEditProductModal}
      />

      <ProductViewPopup
        popupOpen={isViewProductModalOpen}
        onClose={closeViewProductModal}
      />
    </>
  );
};

export default ProductTable;
